import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";
import { lockScroll } from "../lib/scrollLock";
import { useEnquiry, EASE } from "./EnquiryModal";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/domestic-packages", label: "Domestic" },
  { to: "/international-packages", label: "International" },
  { to: "/group-trips", label: "Group Trips" },
  { to: "/customised-trips", label: "Customised" },
  { to: "/about-us", label: "About" },
  { to: "/contact-us", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { openEnquiry } = useEnquiry();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    return lockScroll();
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed inset-x-0 top-0 z-[90] transition-all duration-500 ${
          scrolled ? "glass border-b border-white/5" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6">
          <Link to="/" data-testid="nav-link-home" aria-label="Cosmos Routes home">
            <Logo />
          </Link>

          <nav className="hidden items-center gap-7 xl:flex">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                className={`text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                  pathname === l.to ? "text-aurora" : "text-slate-300 hover:text-aurora"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-plan-trip-btn"
              onClick={() => openEnquiry({ tripType: "Customised Trip" })}
              className="hidden items-center gap-2 rounded-full bg-aurora px-5 py-2.5 text-[13px] font-semibold text-ink transition-all duration-300 hover:shadow-[0_0_30px_rgba(0,245,212,0.4)] hover:-translate-y-0.5 sm:flex"
            >
              Plan My Trip <ArrowUpRight className="h-3.5 w-3.5" />
            </button>
            <button
              data-testid="nav-mobile-menu-btn"
              aria-label="Open menu"
              onClick={() => setOpen(true)}
              className="rounded-full border border-white/15 p-2.5 text-slate-100 xl:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[110] flex flex-col bg-ink/95 backdrop-blur-2xl"
            data-testid="mobile-menu"
          >
            <div className="flex h-20 items-center justify-between px-6">
              <Logo />
              <button
                data-testid="nav-mobile-close-btn"
                aria-label="Close menu"
                onClick={() => setOpen(false)}
                className="rounded-full border border-white/15 p-2.5 text-slate-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-1 px-8">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i + 0.1, duration: 0.5, ease: EASE }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                    onClick={() => setOpen(false)}
                    className={`block py-3 font-display text-3xl font-bold tracking-tight sm:text-4xl ${
                      pathname === l.to ? "text-aurora" : "text-slate-200"
                    }`}
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.5, ease: EASE }}
                data-testid="mobile-plan-trip-btn"
                onClick={() => {
                  setOpen(false);
                  openEnquiry({ tripType: "Customised Trip" });
                }}
                className="mt-8 w-fit rounded-full bg-aurora px-7 py-3.5 font-semibold text-ink"
              >
                Plan My Trip
              </motion.button>
            </nav>
            <p className="px-8 pb-10 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
              Explore · Experience · Belong
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
